import React, { useState } from 'react'
import { X, XCircle, AlertCircle, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'

interface RejectionModalProps {
  isOpen: boolean
  onClose: () => void
  onReject: (reason: string) => Promise<void>
  requestId: string
  equipmentDescription?: string
  problemDescription?: string
  estimatedCost?: number
  urgency?: string
}

export default function RejectionModal({
  isOpen,
  onClose,
  onReject,
  requestId,
  equipmentDescription,
  problemDescription,
  estimatedCost,
  urgency
}: RejectionModalProps) {
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const trimmedReason = reason.trim()
    if (trimmedReason.length < 10) {
      setError('O motivo da rejeição deve ter pelo menos 10 caracteres')
      return
    }
    
    try {
      setLoading(true)
      setError(null)
      
      await onReject(trimmedReason)
      
      toast.success('Requisição rejeitada com sucesso!', {
        duration: 3000,
        position: 'top-right',
      })
      
      // Resetar formulário
      setReason('')
      
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao rejeitar requisição')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      setReason('')
      setError(null)
      onClose()
    }
  }

  const getUrgencyBadgeClass = (urgency?: string) => {
    switch (urgency) {
      case 'baixa':
        return 'bg-gray-100 text-gray-800'
      case 'media':
        return 'bg-yellow-100 text-yellow-800'
      case 'alta':
        return 'bg-orange-100 text-orange-800'
      case 'critica':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getUrgencyLabel = (urgency?: string) => {
    switch (urgency) {
      case 'baixa':
        return 'Baixa'
      case 'media':
        return 'Média'
      case 'alta':
        return 'Alta'
      case 'critica':
        return 'Crítica'
      default:
        return urgency || 'N/A'
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Overlay */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />
        
        {/* Modal */}
        <div data-testid="rejection-modal" className="relative w-full max-w-md transform rounded-lg bg-white p-6 shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-500" />
              <h2 className="text-lg font-semibold text-brand-text-primary">
                Rejeitar Requisição
              </h2>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="rounded-full p-1 text-gray-400 hover:bg-brand-surface-100 hover:text-brand-text-secondary disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="space-y-4">
            {/* Informações da solicitação */}
            <div className="bg-brand-surface-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-brand-text-primary mb-1">
                {equipmentDescription || `Solicitação #${requestId?.substring(0, 8)}...`}
              </p>
              {problemDescription && (
                <p className="text-sm text-brand-text-secondary mb-2">
                  {problemDescription.length > 100 
                    ? `${problemDescription.substring(0, 100)}...` 
                    : problemDescription}
                </p>
              )}
              <div className="flex items-center gap-4 text-xs text-brand-text-muted">
                {estimatedCost && (
                  <span>
                    Custo: <span className="font-medium text-brand-text-secondary">€{estimatedCost.toFixed(2)}</span>
                  </span>
                )}
                {urgency && (
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getUrgencyBadgeClass(urgency)}`}>
                    {getUrgencyLabel(urgency)}
                  </span>
                )}
              </div>
            </div>

            {/* Formulário */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Motivo da rejeição */}
              <div>
                <label className="block text-sm font-medium text-brand-text-primary mb-1">
                  Motivo da Rejeição <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  placeholder="Explique detalhadamente o motivo da rejeição..."
                  data-testid="rejection-reason"
                  className="w-full rounded-md border-brand-neutral-300 shadow-sm focus:border-brand-primary-500 focus:ring-brand-primary-500"
                  disabled={loading}
                  required
                />
                {reason.trim().length > 0 && reason.trim().length < 10 && (
                  <p className="text-sm text-red-600 mt-1">O motivo deve ter pelo menos 10 caracteres</p>
                )}
              </div>

              {/* Erro */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                  <AlertCircle className="h-4 w-4 text-red-400" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Botões */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className="flex-1 px-4 py-2 text-sm font-medium text-brand-text-primary bg-white border border-brand-neutral-300 rounded-md hover:bg-brand-surface-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading || reason.trim().length < 10}
                  data-testid="rejection-confirm-btn"
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Rejeitar...
                    </div>
                  ) : (
                    'Confirmar Rejeição'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}